import React, { useEffect, useMemo, useState } from "react";

import Badge from "../../../Components/Badge";
import Button from "../../../Components/Button";
import CustomCheckbox from "../../../Components/CheckCircle";
import CommonModal from "../../../Components/Modal";
import Select from "../../../Components/Select";
import { useAppDispatch } from "../../../Store/Hooks";
import {
  fetchSuccessorCandidates,
  type TeamViewsAssignment,
  type TeamViewsPerson,
} from "../../../Store/Reducers/TeamViewsSlice";

type Selection = { account_id: string; successor_user_id?: string };

type SuccessorOption = {
  user_id: string;
  full_name?: string | null;
  role_name?: string | null;
  coverage_role?: string | null;
  is_same_role?: boolean;
  active_contact_ids?: string[];
};

type AccountCandidatePool = {
  candidates: SuccessorOption[];
  departingContactIds: string[];
};

/** Keep successors who can take at least one pair (matrix bulk-transfer rule). */
function filterTransferableSuccessors(
  candidates: SuccessorOption[],
  departingContactIds: string[]
): SuccessorOption[] {
  if (!departingContactIds.length) return candidates;
  const departing = departingContactIds.map(String);
  return candidates.filter(candidate => {
    const covered = new Set((candidate.active_contact_ids || []).map(String));
    return departing.some(contactId => !covered.has(contactId));
  });
}

type ReassignModalProps = {
  open: boolean;
  person: TeamViewsPerson | null;
  preview: any;
  /** When set, only this account is shown / acted on (from Assigned accounts). */
  focusAccountId?: string | null;
  /** Stack above another open modal (e.g. Account Workspace See All). */
  nested?: boolean;
  onClose: () => void;
  onPreview: (selections: Selection[]) => Promise<void>;
  onConfirm: (selections: Selection[]) => Promise<void>;
};

/** Primary/secondary sponsor holders cannot be reassigned from Team Views. */
export function isAssignmentReassignable(a: TeamViewsAssignment): boolean {
  if (a.reassign_eligible === false) return false;
  const key = (a.role_key || "").trim().toUpperCase();
  if (key === "SPONSOR") return false;
  const name = (a.role_name || "").trim().toLowerCase();
  if (
    name === "sponsor" ||
    name === "primary sponsor" ||
    name === "secondary sponsor"
  ) {
    return false;
  }
  return true;
}

const ReassignModal: React.FC<ReassignModalProps> = ({
  open,
  person,
  preview,
  focusAccountId = null,
  nested = false,
  onClose,
  onPreview,
  onConfirm,
}) => {
  const dispatch = useAppDispatch();
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [successors, setSuccessors] = useState<Record<string, string>>({});
  const [candidatesByAccount, setCandidatesByAccount] = useState<
    Record<string, AccountCandidatePool>
  >({});
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [step, setStep] = useState<"pick" | "preview">("pick");
  const [busy, setBusy] = useState(false);

  const eligibleAssignments = useMemo(() => {
    if (!person) return [];
    const all = person.assignments.filter(isAssignmentReassignable);
    if (!focusAccountId) return all;
    return all.filter(a => String(a.account_id) === String(focusAccountId));
  }, [person, focusAccountId]);

  const lockedAssignments = useMemo(() => {
    if (!person) return [];
    const all = person.assignments.filter(a => !isAssignmentReassignable(a));
    if (!focusAccountId) return all;
    // Single-account flow only shows the focused eligible row — hide other locks.
    return [];
  }, [person, focusAccountId]);

  useEffect(() => {
    if (!open || !person) return;
    if (focusAccountId && eligibleAssignments.length === 1) {
      setChecked({ [eligibleAssignments[0].account_id]: true });
    } else {
      setChecked({});
    }
    setSuccessors({});
    setStep("pick");
  }, [open, person, eligibleAssignments, focusAccountId]);

  const handleSelectAll = () => {
    const nextChecked: Record<string, boolean> = {};
    eligibleAssignments.forEach(a => {
      nextChecked[a.account_id] = true;
    });
    setChecked(nextChecked);
  };

  const handleClearAll = () => {
    setChecked({});
  };

  // Same pool as matrix Transfer all, then drop successors who already cover
  // every contact the person owns (BulkTransfer "already covers this" — nothing
  // left to transfer).
  useEffect(() => {
    if (!open || !person?.user_id || !eligibleAssignments.length) {
      setCandidatesByAccount({});
      setCandidatesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setCandidatesLoading(true);

    (async () => {
      try {
        const entries = await Promise.all(
          eligibleAssignments.map(async assignment => {
            try {
              const data = await dispatch(
                fetchSuccessorCandidates({
                  accountId: assignment.account_id,
                  departingUserId: person.user_id,
                })
              ).unwrap();
              const departingContactIds = (
                data?.departing_active_contact_ids || []
              ).map(String);
              const list = filterTransferableSuccessors(
                (data?.candidates || []) as SuccessorOption[],
                departingContactIds
              );
              return [
                assignment.account_id,
                { candidates: list, departingContactIds },
              ] as const;
            } catch {
              return [
                assignment.account_id,
                {
                  candidates: [] as SuccessorOption[],
                  departingContactIds: [],
                },
              ] as const;
            }
          })
        );
        if (!cancelled) {
          setCandidatesByAccount(Object.fromEntries(entries));
          setSuccessors(prev => {
            const next = { ...prev };
            for (const [accountId, pool] of entries) {
              const selected = next[accountId];
              if (!selected) continue;
              const stillValid = pool.candidates.some(
                c => String(c.user_id) === String(selected)
              );
              if (!stillValid) delete next[accountId];
            }
            return next;
          });
        }
      } finally {
        if (!cancelled) setCandidatesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, person?.user_id, eligibleAssignments, dispatch]);

  const selections = useMemo(() => {
    return eligibleAssignments
      .filter(a => checked[a.account_id])
      .map(a => ({
        account_id: a.account_id,
        successor_user_id: successors[a.account_id] || undefined,
      }));
  }, [eligibleAssignments, checked, successors]);

  if (!open || !person) return null;

  const handlePreview = async () => {
    setBusy(true);
    try {
      await onPreview(selections);
      setStep("preview");
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm(selections);
    } finally {
      setBusy(false);
    }
  };

  const previewAccounts = preview?.accounts || [];
  const previewHasSuccessor = previewAccounts.some(
    (block: { successor_name?: string | null }) => Boolean(block.successor_name)
  );
  const previewHasRemove = previewAccounts.some(
    (block: { successor_name?: string | null }) => !block.successor_name
  );
  let previewConfirmLabel = "Confirm reassign";
  if (previewHasRemove && previewHasSuccessor) {
    previewConfirmLabel = "Confirm changes";
  } else if (previewHasRemove) {
    previewConfirmLabel = "Confirm remove";
  }

  const pickFooter = (
    <>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handlePreview}
        disabled={busy || !selections.length}
        loading={busy}
      >
        Preview impact
      </Button>
    </>
  );

  const previewFooter = (
    <>
      <Button variant="secondary" onClick={() => setStep("pick")}>
        Back
      </Button>
      <Button
        variant="primary"
        onClick={handleConfirm}
        disabled={busy}
        loading={busy}
      >
        {previewConfirmLabel}
      </Button>
    </>
  );

  return (
    <CommonModal
      isOpen={open}
      toggle={onClose}
      title={`Reassign / Remove — ${person.full_name}`}
      size="lg"
      usePortal
      nested={nested}
      footer={step === "pick" ? pickFooter : previewFooter}
    >
      {step === "pick" ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Choose accounts to reassign and pick a successor from that
            account&apos;s vendor team (same-role teammates are recommended).
            Unchecked accounts are left out of this action.
          </p>
          <p
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            role="note"
          >
            <span className="font-semibold">
              Remove from account / Leave unassigned
            </span>{" "}
            removes this person from the selected account(s). Their coverage
            pairs become open holes and they{" "}
            <span className="font-semibold">will</span> appear on Departed Row.
          </p>

          {eligibleAssignments.length > 0 && !focusAccountId ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline-primary"
                type="button"
                onClick={handleSelectAll}
              >
                Select All
              </Button>
              <Button
                variant="outline-primary"
                type="button"
                onClick={handleClearAll}
              >
                Clear All
              </Button>
            </div>
          ) : null}

          {eligibleAssignments.map(a => {
            const pool = candidatesByAccount[a.account_id];
            const candidates = pool?.candidates || [];
            // Match matrix Transfer all grouping after collision filter.
            const recommended = candidates.filter(c => c.is_same_role);
            const others = candidates.filter(c => !c.is_same_role);
            const optionLabel = (c: SuccessorOption) =>
              `${c.full_name || c.user_id} — ${
                c.coverage_role || c.role_name || "Team"
              }`;
            const groups = [
              recommended.length
                ? {
                    label: "Recommended",
                    options: recommended.map(c => ({
                      label: optionLabel(c),
                      value: String(c.user_id),
                    })),
                  }
                : null,
              others.length
                ? {
                    label: "Other owners",
                    options: others.map(c => ({
                      label: optionLabel(c),
                      value: String(c.user_id),
                    })),
                  }
                : null,
            ].filter(Boolean) as {
              label: string;
              options: { label: string; value: string }[];
            }[];
            const optionCount = recommended.length + others.length;
            let successorPlaceholder = "Remove from account / Leave unassigned";
            if (candidatesLoading) {
              successorPlaceholder = "Loading vendor team…";
            } else if (!optionCount) {
              successorPlaceholder = pool?.departingContactIds?.length
                ? "No transferable owners (already covered)"
                : "No other owners on this account";
            }
            const isChecked = Boolean(checked[a.account_id]);

            return (
              <div
                key={a.account_id}
                className={`rounded-xl border p-4 shadow-sm transition-colors ${
                  isChecked
                    ? "border-purple-200 bg-purple-50/40"
                    : "border-gray-200 bg-white"
                }`}
              >
                <CustomCheckbox
                  checked={isChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setChecked(prev => ({
                      ...prev,
                      [a.account_id]: e.target.checked,
                    }))
                  }
                  label={`${a.account_name || a.account_id}${
                    a.role_name ? ` (${a.role_name})` : ""
                  }`}
                />
                <div className="mt-3 pl-7">
                  <Select
                    name={`successor-${a.account_id}`}
                    value={successors[a.account_id] || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSuccessors(prev => ({
                        ...prev,
                        [a.account_id]: e.target.value,
                      }))
                    }
                    placeholder={successorPlaceholder}
                    options={[]}
                    groups={groups}
                    disabled={!isChecked || candidatesLoading}
                    className="mb-0"
                  />
                </div>
              </div>
            );
          })}

          {lockedAssignments.map(a => (
            <div
              key={a.account_id}
              className="rounded-xl border border-dashed border-purple-200 bg-purple-50/20 p-4"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-gray-800">
                  {a.account_name || a.account_id}
                  {a.role_name ? (
                    <span className="font-normal text-gray-500">
                      {" "}
                      ({a.role_name})
                    </span>
                  ) : null}
                </span>
                <Badge label="Locked" type="long-assessment-purple" />
              </div>
              <p className="mt-2 text-xs leading-relaxed text-gray-500">
                Primary/secondary sponsor on this account — cannot be reassigned
                from Team Views.
              </p>
            </div>
          ))}

          {!eligibleAssignments.length && !lockedAssignments.length ? (
            <p className="text-sm text-gray-500">
              No accounts available for this person.
            </p>
          ) : null}

          {!eligibleAssignments.length && lockedAssignments.length > 0 ? (
            <p className="rounded-lg border border-purple-100 bg-purple-50/50 px-3 py-2 text-sm text-gray-600">
              No reassignable accounts — only primary/secondary sponsor
              assignments remain.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Review what will happen for each selected account before confirming.
          </p>
          {(preview?.accounts || []).some(
            (block: { successor_name?: string | null }) => !block.successor_name
          ) ? (
            <p
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              role="note"
            >
              Accounts with <span className="font-semibold">No successor</span>{" "}
              remove this person from the account and leave open holes on{" "}
              <span className="font-semibold">Departed Row</span>.
            </p>
          ) : null}
          {(preview?.accounts || []).map((block: any) => {
            const hasSuccessor = Boolean(block.successor_name);
            const pairs = block.pairs || [];
            return (
              <div
                key={block.account_id}
                className={`rounded-xl border p-4 shadow-sm ${
                  hasSuccessor
                    ? "border-purple-100 bg-white"
                    : "border-purple-200 bg-purple-50/40"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {block.account_name}
                  </span>
                  {hasSuccessor ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800">
                      <span className="text-purple-400" aria-hidden>
                        →
                      </span>
                      {block.successor_name}
                    </span>
                  ) : (
                    <Badge label="No successor" type="long-assessment-purple" />
                  )}
                </div>

                {!hasSuccessor ? (
                  <p className="mt-2 text-xs text-amber-800">
                    Person removed from this account · pairs orphaned → Departed
                    Row
                  </p>
                ) : null}

                {pairs.length > 0 ? (
                  <ul className="mt-3 divide-y divide-purple-100/80 overflow-hidden rounded-lg border border-purple-100 bg-white">
                    {pairs.map((pair: any) => {
                      const isTransfer = pair.outcome === "transfer";
                      return (
                        <li
                          key={pair.relationship_id}
                          className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-sm"
                        >
                          <span className="text-gray-700">
                            <span className="font-medium text-gray-900">
                              {pair.contact_name || pair.contact_id}
                            </span>
                            <span className="text-gray-400">
                              {" "}
                              ·{" "}
                              {pair.latest_rl != null ? (
                                `RL ${pair.latest_rl}`
                              ) : (
                                <span title="Unassessed">RL ?</span>
                              )}
                            </span>
                          </span>
                          <Badge
                            label={isTransfer ? "Transfers" : "Orphaned"}
                            type={
                              isTransfer ? "success" : "long-assessment-purple"
                            }
                          />
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="mt-2 text-xs text-gray-500">
                    No mapped relationships on this account — membership change
                    only.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </CommonModal>
  );
};

export default ReassignModal;
